import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, BookOpen, Clock, Plus, Crosshair, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const EducationHub = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const categories = [
    'Beginner Crypto',
    'Technical Analysis',
    'Trading Psychology',
    'Tokenomics',
    'On-Chain Analysis',
    'Risk Management',
    'Futures Trading Basics',
    'Stock Market Basics',
    'Macro Economics Simplified',
    'Emerging Trends',
  ];

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await axios.get(`${API}/education/articles`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.summary?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12 flex justify-between items-start">
            <div>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="education-title">
                Education <span className="text-[#C5A059]">Hub</span>
              </h1>
              <p className="text-gray-300 text-lg">
                Learn crypto, trading, and financial intelligence from the ground up.
              </p>
            </div>
            {user && user.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin')}
                className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-3 font-semibold shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
                data-testid="create-article-button"
              >
                <Plus size={20} className="mr-2" />
                Create Article
              </Button>
            )}
          </div>

          {/* Chart Reader promo */}
          <Link
            to="/chart-reader"
            className="block mb-10 group"
            data-testid="chart-reader-promo"
          >
            <div className="relative bg-gradient-to-r from-[#17130B] to-[#0B0A08] border border-[#C5A059]/30 rounded-xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-[#C5A059]/60 transition-colors overflow-hidden">
              <div className="absolute top-3 right-4 text-[10px] font-bold uppercase tracking-widest text-black bg-[#C5A059] px-2 py-0.5 rounded-full">New</div>
              <div className="w-12 h-12 rounded-lg bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center shrink-0">
                <Crosshair className="text-[#C5A059]" size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-manrope font-bold text-lg text-white group-hover:text-[#C5A059] transition-colors">
                  Chart Reader — The Trader Path
                </h3>
                <p className="text-gray-400 text-sm">
                  An interactive game that trains your eyes to read charts like a pro. Fresh charts every round, level-based progression, progress saved.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-[#C5A059] font-semibold text-sm whitespace-nowrap">
                Start Training <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>

          {/* Search & Filters */}
          <div className="mb-8">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm h-12"
                data-testid="search-input"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setSelectedCategory(null)}
                variant={selectedCategory === null ? 'default' : 'outline'}
                className={selectedCategory === null ? 'bg-[#C5A059] text-black hover:bg-[#D4AF37]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                size="sm"
                data-testid="category-all"
              >
                All
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  className={selectedCategory === category ? 'bg-[#C5A059] text-black hover:bg-[#D4AF37]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm"
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 text-lg" data-testid="no-articles-message">
                No articles found. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link
                  key={article.article_id}
                  to={`/education/${article.article_id}`}
                  className="bg-[#111111] border border-white/10 rounded-lg overflow-hidden hover:border-[#C5A059]/50 hover:shadow-lg transition-all duration-300 group hover-lift"
                  data-testid={`article-${article.article_id}`}
                >
                  {article.image && (
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30 text-xs px-2 py-1 rounded-full">
                        {article.category}
                      </span>
                      {article.tags?.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-manrope font-semibold text-white text-lg mb-2 group-hover:text-[#C5A059] transition-colors">
                      {article.title}
                    </h3>
                    {article.summary && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {article.summary}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>By {article.author_name}</span>
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(article.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EducationHub;
